import { type ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { Button } from './Button'
import { removeOverlayHistoryMarker, useOverlayHistoryDismiss } from './useOverlayHistoryDismiss'

const MODAL_HISTORY_MARKER = 'qscModal'
const shouldSkipHistoryBack = (options: unknown) =>
  typeof options === 'object' &&
  options !== null &&
  'skipHistoryBack' in options &&
  Boolean((options as { skipHistoryBack?: boolean }).skipHistoryBack)

interface ModalButton {
  label: string
  variant?: 'solid' | 'soft' | 'outline' | 'ghost' | 'danger'
  onClick?: () => void | Promise<void>
}

interface ModalOptions {
  title?: string
  message?: ReactNode
  content?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  closeOnBackdrop?: boolean
  buttons?: ModalButton[]
  onConfirm?: () => void | Promise<void>
  onCancel?: () => void
}

type Listener = (options: ModalOptions | null) => void

let currentModal: ModalOptions | null = null
const listeners = new Set<Listener>()

function emit() {
  for (const listener of listeners) listener(currentModal)
}

export function openModal(options: ModalOptions) {
  currentModal = options
  emit()
}

export function closeModal(options?: unknown) {
  if (shouldSkipHistoryBack(options)) {
    removeOverlayHistoryMarker(MODAL_HISTORY_MARKER)
  }
  currentModal = null
  emit()
}

export function ModalHost() {
  const [options, setOptions] = useState<ModalOptions | null>(currentModal)
  const [pending, setPending] = useState(false)
  const pendingRef = useRef(false)
  const dismiss = useOverlayHistoryDismiss(Boolean(options), closeModal, MODAL_HISTORY_MARKER)

  useEffect(() => {
    listeners.add(setOptions)
    return () => {
      listeners.delete(setOptions)
    }
  }, [])

  useEffect(() => {
    pendingRef.current = pending
  }, [pending])

  useEffect(() => {
    if (!options) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !pendingRef.current) dismiss()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKeyDown)
      setPending(false)
    }
  }, [dismiss, options])

  if (!options || typeof document === 'undefined') return null

  const run = async (fn?: () => void | Promise<void>) => {
    try {
      setPending(true)
      await fn?.()
      dismiss()
    } finally {
      setPending(false)
    }
  }

  const buttons =
    options.buttons ??
    [
      ...(options.cancelLabel
        ? [{ label: options.cancelLabel, variant: 'outline' as const, onClick: options.onCancel }]
        : []),
      {
        label: options.confirmLabel ?? '확인',
        variant: options.destructive ? ('danger' as const) : ('solid' as const),
        onClick: options.onConfirm,
      },
    ]

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="qsc-modal-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/45 px-3 py-4 sm:px-4 sm:py-6"
      onPointerDown={(event) => {
        if (event.target !== event.currentTarget) return
        if (options.closeOnBackdrop !== false && !pending) dismiss()
      }}
    >
      <div
        className="w-full max-w-md rounded-lg border border-border-subtle bg-surface p-4 shadow-overlay sm:p-5"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <h1 id="qsc-modal-title" className="text-preset-heading text-text">
              {options.title ?? '알림'}
            </h1>
            {options.message && <div className="mt-2.5 text-preset-body text-text-muted sm:mt-3">{options.message}</div>}
          </div>
          <button
            type="button"
            className="flex size-8 shrink-0 items-center justify-center rounded-full text-text-subtle hover:bg-gray-100"
            onClick={dismiss}
            aria-label="닫기"
            disabled={pending}
          >
            <X aria-hidden className="size-4" />
          </button>
        </div>
        {options.content && <div className="mt-3 sm:mt-4">{options.content}</div>}
        {buttons.length > 0 ? (
          <div className="mt-5 flex justify-end gap-2 sm:mt-6">
            {buttons.map((button) => (
              <Button
                key={button.label}
                variant={button.variant}
                loading={pending}
                onClick={() => run(button.onClick)}
              >
                {button.label}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
